if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll('.m-product-option select, .m-product-option input');
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });
      return result;
    }

    connectedCallback() {
      this.addEventListener('change', (event) => {
        this.rehairResolveSelection(event.target);
        const target = this.getInputForEventTarget(event.target);
        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, { data: { selectedOptionValues: this.selectedOptionValues, event, target } });
      });
      this.rehairInit();
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }

    /*
      2026-10-13: products whose option matrix is incomplete (e.g. a size made in 3 of 15 colours).
      Values that do not exist for the options chosen before them are hidden instead of struck through,
      a change that would land on a missing combination moves the later options to one that exists,
      and a product that opens on a size with under half the choices of the fullest size opens on that size.
      Needs the [data-rehair-variant-matrix] JSON that product-variant-picker.liquid prints for such products.
    */
    rehairMatrix() {
      if (this._rehairMatrix !== undefined) return this._rehairMatrix;
      let matrix = null;
      const node = this.querySelector('script[data-rehair-variant-matrix]');
      if (node) {
        try {
          matrix = JSON.parse(node.textContent);
        } catch (_error) {
          matrix = null;
        }
      }
      this._rehairMatrix = matrix && Array.isArray(matrix.o) && matrix.o.length ? matrix : null;
      return this._rehairMatrix;
    }

    rehairGroups() {
      return Array.from(this.querySelectorAll('.m-product-option')).map((el) => ({
        el,
        select: el.querySelector('select'),
        inputs: Array.from(el.querySelectorAll('input[type="radio"]')),
      }));
    }

    rehairInputValue(input) {
      const node = input.closest('.m-product-option--node');
      return node && node.dataset.value !== undefined ? node.dataset.value : input.value;
    }

    rehairValues(group) {
      if (group.select) return Array.from(group.select.options).map((option) => option.value);
      return group.inputs.map((input) => this.rehairInputValue(input));
    }

    rehairValue(group) {
      if (group.select) return group.select.value;
      const checked = group.inputs.find((input) => input.checked);
      return checked ? this.rehairInputValue(checked) : undefined;
    }

    rehairSetValue(group, value) {
      if (group.select) {
        group.select.value = value;
        return;
      }
      const input = group.inputs.find((item) => this.rehairInputValue(item) === value);
      if (input) input.checked = true;
    }

    rehairHas(prefix, availableOnly) {
      const matrix = this.rehairMatrix();
      return matrix.o.some((options, index) => {
        if (availableOnly && matrix.a && matrix.a[index] === false) return false;
        return prefix.every((value, position) => options[position] === value);
      });
    }

    rehairResolveSelection(target) {
      if (!this.rehairMatrix()) return;
      const groups = this.rehairGroups();
      if (groups.length < 2) return;
      const changedIndex = groups.findIndex((group) => target && group.el.contains(target));
      const values = groups.map((group) => this.rehairValue(group));
      if (values.some((value) => value === undefined)) return;
      VariantPicker.rehairPendingNote = null;

      for (let index = Math.max(changedIndex, 0) + 1; index < groups.length; index += 1) {
        const prefix = values.slice(0, index);
        if (this.rehairHas(prefix.concat([values[index]]))) continue;
        const candidates = this.rehairValues(groups[index]).filter((value) => this.rehairHas(prefix.concat([value])));
        if (!candidates.length) break;
        const pick = candidates.find((value) => this.rehairHas(prefix.concat([value]), true)) || candidates[0];
        if (changedIndex >= 0 && !this.rehairSilent && !VariantPicker.rehairPendingNote) {
          VariantPicker.rehairPendingNote = {
            productId: this.dataset.productId,
            index,
            from: values[index],
            context: values[changedIndex],
            to: pick,
          };
        }
        values[index] = pick;
        this.rehairSetValue(groups[index], pick);
      }
    }

    rehairMarkMissing() {
      if (!this.rehairMatrix()) return;
      const groups = this.rehairGroups();
      const values = groups.map((group) => this.rehairValue(group));
      groups.forEach((group, index) => {
        if (index === 0) return;
        const prefix = values.slice(0, index);
        let shown = 0;
        if (group.select) {
          Array.from(group.select.options).forEach((option) => {
            const missing = !this.rehairHas(prefix.concat([option.value]));
            option.hidden = missing;
            option.disabled = missing;
            if (!missing) shown += 1;
          });
        } else {
          group.inputs.forEach((input) => {
            const node = input.closest('.m-product-option--node');
            const missing = !this.rehairHas(prefix.concat([this.rehairInputValue(input)]));
            if (node) node.classList.toggle('rehair-opt-missing', missing);
            if (!missing) shown += 1;
          });
        }

        const count = group.el.querySelector('[data-rehair-shade-count]');
        if (count) count.textContent = String(shown);
        const more = group.el.querySelector('[data-rehair-shade-more]');
        if (!more) return;
        const counts = new Map();
        this.rehairMatrix().o.forEach((options) => counts.set(options[0], (counts.get(options[0]) || 0) + 1));
        const max = Math.max(...counts.values());
        const fuller = this.rehairValues(groups[0]).filter((value) => counts.get(value) === max);
        if (index === 1 && groups.length === 2 && shown < max && fuller.length) {
          more.textContent = (this.dataset.rehairMoreTemplate || 'More shades in [sizes]').replace('[sizes]', fuller.join(', '));
          more.hidden = false;
        } else {
          more.hidden = true;
        }
      });
    }

    rehairShowNote() {
      const note = VariantPicker.rehairPendingNote;
      if (!note || note.productId !== this.dataset.productId) return;
      const group = this.rehairGroups()[note.index];
      if (!group || this.rehairValue(group) !== note.to) return;
      const message = document.createElement('p');
      message.className = 'rehair-variant-note';
      message.setAttribute('role', 'status');
      message.textContent = (this.dataset.rehairAdjustTemplate || '[from] is not made in [context]. Showing [to].')
        .replace('[from]', note.from)
        .replace('[context]', note.context)
        .replace('[to]', note.to);
      group.el.appendChild(message);
    }

    rehairApplyPreferredDefault() {
      if (VariantPicker.rehairDefaultChecked) return;
      if (!this.isConnected || !this.closest("product-info[id^='MainProduct']")) return;
      VariantPicker.rehairDefaultChecked = true;
      if (new URLSearchParams(window.location.search).has('variant')) return;
      const groups = this.rehairGroups();
      if (groups.length < 2) return;
      const counts = new Map();
      this.rehairMatrix().o.forEach((options) => counts.set(options[0], (counts.get(options[0]) || 0) + 1));
      const max = Math.max(...counts.values());
      const current = this.rehairValue(groups[0]);
      if ((counts.get(current) || 0) * 2 >= max) return;
      const best = this.rehairValues(groups[0]).reverse().find((value) => counts.get(value) === max);
      if (!best || best === current) return;
      this.rehairSetValue(groups[0], best);
      const field = groups[0].select || groups[0].inputs.find((input) => input.checked);
      this.rehairSilent = true;
      if (field) field.dispatchEvent(new Event('change', { bubbles: true }));
      this.rehairSilent = false;
    }

    rehairInit() {
      if (!this.rehairMatrix()) return;
      this.rehairMarkMissing();
      this.rehairShowNote();
      if (!VariantPicker.rehairDefaultChecked) {
        customElements.whenDefined('product-info').then(() => {
          window.setTimeout(() => this.rehairApplyPreferredDefault(), 60);
        });
      }
    }
  }
  customElements.define("variant-picker", VariantPicker);
}
